import React, { forwardRef, useContext } from 'react';
import styled from 'styled-components';
import { GetColor, Icon, ZIndex } from 'venn-ui-kit';
import { type HTMLProps } from '../factory';
import { useAppPrintMode } from '../../../../print';
import { UserContext } from '../../../../contexts';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { isReportState, isSelectedBlockState, selectedBlocksState } from 'venn-state';
import { DRAG_HANDLE_CLASS } from '../../../../utils';
import { Checkbox } from '@/components/ui/checkbox';
import { SpecialCssClasses, useHasFF } from 'venn-utils';

export type BlockHeaderTopContainerElement = React.ElementRef<'div'>;
export interface BlockHeaderTopContainerProps extends HTMLProps<'div'> {
  children?: React.ReactNode;
  id: string;
}

export const BlockHeaderTopContainer = forwardRef<BlockHeaderTopContainerElement, BlockHeaderTopContainerProps>(
  (props, ref) => {
    const { children, id } = props;
    const isReport = useRecoilValue(isReportState);
    const { inPrintMode, inPrintModeOrReportLab } = useAppPrintMode();
    const { hasPermission } = useContext(UserContext);
    const isBlockSelected = useRecoilValue(isSelectedBlockState(id));
    const hasBlockAssignment = useHasFF('block_assignment_ff');

    const onCheckBlock = useRecoilCallback(
      ({ set }) =>
        (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          if (isBlockSelected) {
            set(selectedBlocksState, (current) => current.filter((otherId) => otherId !== id));
          } else {
            set(selectedBlocksState, (current) => [...current, id]);
          }
        },
      [isBlockSelected, id],
    );

    return (
      <Container ref={ref} inPrintMode={inPrintModeOrReportLab}>
        <div className="flex-1">{children}</div>
        <div>
          <div className="flex gap-[0.5rem] items-center">
            {hasBlockAssignment && !inPrintMode && (
              <StyledCheckbox state={isBlockSelected ? 'checked' : 'unchecked'} onClick={onCheckBlock} />
            )}
            {isReport && !inPrintMode && hasPermission('STUDIO_REORDER_BLOCKS') && (
              <DragHandle type="arrows" className={DRAG_HANDLE_CLASS} />
            )}
          </div>
        </div>
      </Container>
    );
  },
);

BlockHeaderTopContainer.displayName = 'BlockHeaderTopContainer';

const Container = styled.div<{ inPrintMode: boolean }>`
  display: flex;
  padding: ${({ inPrintMode }) => `${inPrintMode ? '0.7rem 0.7rem 0.3rem 0.7rem' : '13px 15px 5px 15px'}`};

  label {
    font-size: 14px;
    padding-right: 5px;
  }

  border-bottom: 1px solid ${GetColor.Grey};
`;

const StyledCheckbox = styled(Checkbox)`
  .${SpecialCssClasses.ExportAsImage} & {
    display: none;
  }
`;

export const DragHandle = styled(Icon)`
  color: rgba(0, 0, 0, 0.6);
  font-size: 1rem;
  cursor: grab;
  z-index: ${ZIndex.Cover};
`;
